import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import QRCode from 'qrcode'
import { DateTime } from 'luxon';

import useQuery from 'app/hooks/useQuery';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import ButtonGroup from '@material-ui/core/ButtonGroup';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelActions from '@material-ui/core/ExpansionPanelActions';

import Skeleton from '@material-ui/lab/Skeleton';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import InviteGuestDialog from 'app/components/event/InviteGuestDialog';
import PurchaseTicketDialog from 'app/components/accounting/payments/PurchaseTicketDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    paperSkeleton: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(3),
        display: 'flex',
    },
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
    },
    pageTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    textSuccess: {
        color: theme.palette.success.main
    },
    textWarning: {
        color: theme.palette.warning.main
    },
    panel: {
        position: 'relative'
    },
    qrContainer: {
        textAlign: 'center'
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
}));

function Invitation(props) {
    const { initInvitation, initId } = props;

    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { app } = useApp();
    const api = useApi();

    const [ invitation, setInvitation ] = useState(null);
    const [ qrCode, setQrCode ] = useState(null);
    const [ loading, setLoading ] = useState(false);

    const getQR = () => {
        QRCode.toDataURL(invitation.qr_code).then(url => setQrCode(url));
    }

    useEffect(() => {

        if (initInvitation) setInvitation(initInvitation);

    }, []);

    useEffect(() => {
        if (invitation) getQR();
    }, [invitation])

    const postAccept = () => {

        setLoading(true)

        api.get(`/invitation/${invitation.id}/accept`)
            .then((res) => setInvitation(res.invitation))
            .finally(() => setLoading(false))
    }

    const postMaybe = () => {

        setLoading(true)

        api.get(`/invitation/${invitation.id}/maybe`)
            .then((res) => setInvitation(res.invitation))
            .finally(() => setLoading(false))
    }

    const postDecline = () => {

        setLoading(true)

        api.get(`/invitation/${invitation.id}/decline`)
            .then((res) => setInvitation(res.invitation))
            .finally(() => setLoading(false))
    }

    return invitation ? (
        <ExpansionPanel key={invitation.id} defaultExpanded={invitation.id == initId} className={classes.panel}>
            <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={"invite-" + invitation.id + "-content"}
                id={"invite-" + invitation.id + "-header"}
                >
                <Grid container>
                    <Grid item xs={12} md={4}>
                        <Typography
                            className={classes.heading}
                            gutterBottom={isMobile ? true : false}
                            noWrap={isMobile ? false : true}
                            >
                            From: <strong>{invitation.user ? invitation.user.full_name : app.club.name}</strong>
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography className={classes.secondaryHeading}>
                            {invitation.title}&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                            {(invitation.status == 'accepted') ? (
                                <Typography className={classes.textSuccess} display="inline">
                                    Accepted
                                </Typography>
                            ) : (invitation.status == 'declined') ? (
                                <Typography color="error" display="inline">
                                    Declined
                                </Typography>
                            ) : (invitation.status == 'pending') ? (
                                <Typography className={classes.textWarning} display="inline">
                                    Awaiting Response
                                </Typography>
                            ) : (
                                <Typography className={classes.textWarning} display="inline">
                                    Marked as Maybe
                                </Typography>
                            )}
                        </Typography>
                    </Grid>
                </Grid>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
                <Loading isLoading={loading}/>
                <Grid container>
                    <Grid item xs={12} md={4}>
                        {qrCode &&
                            <div className={classes.qrContainer}>
                                <Typography variant="h1" align="center" className={classes.marginTop}>
                                    <img src={qrCode} />
                                </Typography>
                                <Typography variant="caption" className={classes.marginTop}>
                                    *You may be asked to show this<br/> QR code when entering the event.
                                </Typography>
                            </div>
                        }
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Typography component="div" variant="subtitle2" gutterBottom>Message:</Typography>
                        {(invitation.message != "") ? (
                            <Typography variant="body1" color="textSecondary" gutterBottom>{invitation.message}</Typography>
                        ) : (
                            <Typography variant="body1" color="textSecondary" gutterBottom>This invitation has no message.</Typography>
                        )}
                        <div className={classes.detailsContainer}>
                            <div className={classes.detail}>
                                <Typography component="span" variant="subtitle2" gutterBottom>Event:</Typography>
                                <Typography variant="body1" color="textSecondary" gutterBottom>
                                    {invitation.event.name}
                                </Typography>
                            </div>
                            <div className={classes.detail}>
                                <Typography component="span" variant="subtitle2" gutterBottom>When:</Typography>
                                <Typography variant="body1" color="textSecondary" gutterBottom>
                                    {DateTime.fromISO(invitation.event.starts_at).toLocaleString(DateTime.DATETIME_MED)}
                                </Typography>
                            </div>
                            <div className={classes.detail}>
                                <Typography component="span" variant="subtitle2" gutterBottom>Where:</Typography>
                                <Typography variant="body1" color="textSecondary" gutterBottom>
                                    {invitation.event.location}
                                </Typography>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </ExpansionPanelDetails>
            <Divider />
            <ExpansionPanelActions>
                <Grid container justify="space-between" alignItems="center" spacing={3}>
                    <Grid item>
                        <Button component={RouterLink} to={`/events/${invitation.event.id}`} color="secondary" variant="outlined">
                            Event Details
                        </Button>
                    </Grid>
                    <Grid item>
                        <ButtonGroup variant="outlined" className={classes.buttonGroup} color="secondary">
                            <Button variant={(invitation.status === 'accepted') ? 'contained' : null} onClick={postAccept} >Accept</Button>
                            <Button variant={(invitation.status === 'maybe') ? 'contained' : null} onClick={postMaybe}>Maybe</Button>
                            <Button variant={(invitation.status === 'declined') ? 'contained' : null} onClick={postDecline}>Decline</Button>
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </ExpansionPanelActions>
        </ExpansionPanel>
    ) : null;
}

function InvitationPanelSent(props) {
    const { value, index, invitations, loading, initId, postWithdraw, purchaseGuestTicket, openInviteGuestDialog, ...other } = props;
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('sm'));
    const addBreak = useMediaQuery('(max-width:700px)');
    const classes = useStyles();

    return (
        <Typography className={classes.panelContainer}
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`invitation-tab-${index}`}
            aria-labelledby={`invitation-tab-${index}`}
            {...other}
            >
            {loading ? (
                <Fragment>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="50%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="50%"/>
                        </div>
                    </Paper>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="30%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="60%"/>
                        </div>
                    </Paper>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="60%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="40%"/>
                        </div>
                    </Paper>
                </Fragment>
            ) : (invitations.length > 0) ? invitations.map((i, index) => (
                <ExpansionPanel key={i.id} defaultExpanded={i.id == initId} TransitionProps={{ unmountOnExit: true }}>
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={"invite-" + i.id + "-content"}
                        id={"invite-" + i.id + "-header"}
                        >
                        <Grid container>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    className={classes.heading}
                                    gutterBottom={matches ? true : false}
                                    noWrap={matches ? false : true}
                                    >
                                    To: <strong>{i.guest.full_name}</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Typography className={classes.secondaryHeading}>
                                    {i.event.title}&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                                    {(i.status == 'accepted') ? (
                                        <Typography className={classes.textSuccess} component="span">
                                            Accepted
                                        </Typography>
                                    ) : (i.status == 'declined') ? (
                                        <Typography color="error" component="span">
                                            Declined
                                        </Typography>
                                    ) : (i.status == 'pending') ? (
                                        <Typography className={classes.textWarning} component="span">
                                            Awaiting Response
                                        </Typography>
                                    ) : (
                                        <Typography className={classes.textWarning} component="span">
                                            Marked as Maybe
                                        </Typography>
                                    )}
                                </Typography>

                            </Grid>
                        </Grid>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails>
                        <Grid container>
                            <Hidden smDown>
                                <Grid item xs={false} md={4}>
                                    <Typography component="span" variant="subtitle2" gutterBottom>Guest Details:</Typography><br/>
                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                        {i.guest.full_name}
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary">
                                        {i.guest.address_one} {i.guest.address_two ? i.guest.address_two : ""}
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                        {i.guest.city} {i.guest.state} {i.guest.zip}
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" gutterBottom>
                                        <a href="{i.guest.email}" target="_blank">
                                            {i.guest.email}
                                        </a>
                                    </Typography>
                                    {i.event.guest_tickets_required &&
                                        <Typography color="error" variant="caption">
                                            *Your guest is required to have a ticket for this event.
                                        </Typography>
                                    }
                                </Grid>
                            </Hidden>
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" gutterBottom>Title:</Typography>
                                <Typography variant="body1" color="textSecondary" gutterBottom>{i.title}</Typography>
                                <div className="p-t-m"></div>
                                <Typography variant="subtitle2" component="div" gutterBottom>
                                    Message:
                                </Typography>
                                {(i.message && i.message != "") ? (
                                    <Typography variant="body1" color="textSecondary" gutterBottom>{i.message}</Typography>
                                ) : (
                                    <Typography variant="body1" color="textSecondary" gutterBottom>This invitation has no message.</Typography>
                                )}
                                {(i.ticket) &&
                                    <div className={classes.detailsContainer}>
                                        <div className={classes.detail}>
                                            <Typography component="span" variant="subtitle2" gutterBottom>Ticket:</Typography>
                                            <Typography variant="body2" color="secondary" gutterBottom>
                                                {i.ticket.title}
                                            </Typography>
                                        </div>
                                        <div className={classes.detail}>
                                            <Typography component="span" variant="subtitle2" gutterBottom>Ticket Description:</Typography>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                {i.ticket.description}
                                            </Typography>
                                        </div>
                                    </div>
                                }
                            </Grid>
                        </Grid>
                    </ExpansionPanelDetails>
                    <Divider />
                    <ExpansionPanelActions classes={{ root: classes.panelActions }}>
                        <Grid container justify="space-between" alignItems="center" spacing={2}>
                            <Grid item>
                                <Button
                                    color="primary"
                                    onClick={(e) => postWithdraw(index)}
                                    >
                                    Withdraw Invitation
                                </Button>
                            </Grid>
                            <Grid item>
                                <Link to={"/events/" + i.event.id}>
                                    <Button color="primary">
                                        Event Details
                                    </Button>
                                </Link>&nbsp;&nbsp;&nbsp;
                                {addBreak &&
                                    <br/>
                                }
                                {(i.ticket && i.ticket.invoice && i.ticket.invoice.amount_due > 0) &&
                                    <Button
                                        onClick={(e) => purchaseGuestTicket(i.ticket, i.guest)}
                                        variant="contained"
                                        color="primary"
                                        >
                                        Purchase Ticket for Guest
                                    </Button>
                                }
                            </Grid>
                        </Grid>
                    </ExpansionPanelActions>
                </ExpansionPanel>
            )) : (
                <Typography
                    align="center"
                    color="textSecondary"
                    variant="subtitle2"
                    className={classes.empty}
                    >
                    You haven't sent any invitations yet. Select an event to invite a guest.
                </Typography>
            )}
        </Typography>
    )
}

function InvitationPanelReceived(props) {

    const classes = useStyles();
    const { value, index, invitations, loading, initId, ...other } = props;

    return (
        <Typography className={classes.panelContainer}
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`invitation-tab-${index}`}
            aria-labelledby={`invitation-tab-${index}`}
            {...other}
            >
            {loading ? (
                <Fragment>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="50%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="50%"/>
                        </div>
                    </Paper>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="30%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="60%"/>
                        </div>
                    </Paper>
                    <Paper className={classes.paperSkeleton} elevation={2}>
                        <div className={classes.column}>
                            <Skeleton variant="text" height={6} width="60%"/>
                        </div>
                        <div className={classes.twoColumn}>
                            <Skeleton variant="text" height={6} width="40%"/>
                        </div>
                    </Paper>
                </Fragment>
            ) : (invitations.length > 0) ? invitations.map((i, index) => <Invitation key={index} initInvitation={i} initId={initId}/>
            ) : (
                <Typography
                    align="center"
                    color="textSecondary"
                    variant="subtitle2"
                    className={classes.empty}
                    >
                    You have not received any invitations at this time.
                </Typography>
            )}
        </Typography>
    )
}

function a11yProps(index) {
  return {
    id: 'invitation-tab-${index}',
    'aria-controls': 'invitation-panel-${index}',
  };
}

export default function Invitations(props) {

    const [ invitationsSent, setInvitationsSent ] = useState(false);
    const [ invitationsReceived, setInvitationsReceived ] = useState(false);

    const [ initId, setInitId ] = useState(0);

    const [ activeTab, setActiveTab ] = useState(0);
    const [ loading, setLoading ] = useState(false);

    const [ withdrawIndex, setWithdrawIndex ] = useState(false);
    const [ verifyWithdrawDialogOpen, setVerifyWithdrawDialogOpen ] = useState(false);
    const [ withdrawLoading, setWithdrawLoading ] = useState(false);

    const [ purchasingTicket, setPurchasingTicket ] = useState(false);
    const [ purchasingGuest, setPurchasingGuest ] = useState(false);
    const [ purchaseTicketDialogOpen, setPurchaseTicketDialogOpen ] = useState(false);

    const [ inviteGuestDialogOpen, setInviteGuestDialogOpen ] = useState(false);

    const classes = useStyles();
    const query = useQuery();
    const api = useApi();

    let history = useHistory();

    // Getter Functions ------------------------------------------

    const getInvitations = () => {

        setLoading(true);

        api.get('/invitation')
            .then(res => {
                const newInvitationsSent = res.invitations['sent'];
                const newInvitationsReceived = res.invitations['received'];

                setInvitationsSent(newInvitationsSent);
                setInvitationsReceived(newInvitationsReceived);
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getInvitations();

        if (query.get('tab') == 'sent') setActiveTab(1);
        if (query.get('id')) setInitId(query.get('id'));
    }, []);

    // General Functions

    const handleTabs = (event, newTab) => {
        if (newTab == 1) history.push({ search: '?tab=sent'});
        if (newTab == 0) history.push({ search: '?tab=received'});
        setActiveTab(newTab);
    };


    // Handle withdrawing an invitation

    const openWithdrawDialog = index => {
        setWithdrawIndex(index);
        setVerifyWithdrawDialogOpen(true);
    }

    const postWithdraw = () => {
        setWithdrawLoading(true);

        api.delete('/invitation/' + invitationsSent[withdrawIndex].id )
            .then(res => {
                handleWithdrawCancel();
                getInvitations();
            }).catch(error => {
                console.log(error.response);
            }).then(() => {
                setWithdrawLoading(false);
            });
    }

    const handleWithdrawCancel = () => {
        setWithdrawIndex(false);
        setVerifyWithdrawDialogOpen(false);
    }

    // Handle new guest invitation

    const openInviteGuestDialog = () => {
        setInviteGuestDialogOpen(true)
    }

    const closeInviteGuestDialog = () => {
        getInvitations();
        setInviteGuestDialogOpen(false);
    }

    // Handle Purchase Ticket Dialog

    const openPurchaseTicketDialog = (ticket, guest = false) => {
        setPurchaseTicketDialogOpen(true);
        setPurchasingTicket(ticket);
        setPurchasingGuest(guest);
    }

    const closePurchaseTicketDialog = () => {
        getInvitations();
        setPurchaseTicketDialogOpen(false);
    }

    const closedPurchaseTicketDialog = () => {
        setPurchasingTicket(false);
    }

    return (
        <div className={classes.container}>
            <Typography align="center" variant="h4" component="h2" className={classes.pageTitle}>
                Invitations
            </Typography>
            <div className={classes.marginBottom}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabs}
                    indicatorColor="secondary"
                    textColor="secondary"
                    centered
                    aria-label="Invitations Navigation"
                    >
                    <Tab label="Invitations Received" {...a11yProps(0)}/>
                    <Tab label="Invitations Sent" {...a11yProps(1)}/>
                </Tabs>
                <Divider />
            </div>
            <InvitationPanelReceived
                value={activeTab}
                index={0}
                invitations={invitationsReceived}
                loading={loading}
                initId={initId}
                purchaseTicket={openPurchaseTicketDialog}
            />
            <InvitationPanelSent
                value={activeTab}
                index={1}
                invitations={invitationsSent}
                loading={loading}
                initId={initId}
                postWithdraw={openWithdrawDialog}
                purchaseGuestTicket={openPurchaseTicketDialog}
                openInviteGuestDialog={openInviteGuestDialog}
            />
            <InviteGuestDialog open={inviteGuestDialogOpen} onClose={closeInviteGuestDialog}/>
            <Dialog
                open={verifyWithdrawDialogOpen}
                onClose={handleWithdrawCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                >
                <Loading isLoading={withdrawLoading}/>
                <DialogTitle id="alert-dialog-title">Withdraw Invitation?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to withdraw your invitation?<br/>
                        If you or your guest have purchased a ticket, it may not be eligible for a refund.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleWithdrawCancel} color="default" autoFocus>
                        Cancel
                    </Button>
                    <Button onClick={postWithdraw} color="primary" variant="contained">
                        Withdraw
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
